import React, {Component, ReactElement, ReactNode} from "react";
import {Backdrop, Box, Dialog, IconButton, Theme, ThemeProvider} from "@mui/material";
import {black, lightGray, white} from "./colors";
import {PD_XSM, SZ_SM} from "./dimens";
import {Close} from "@mui/icons-material";
import {md5_uuid} from "./md5";

export const DIALOG_FLAG_DISABLE_BACKDROP_CLICK = 1 << 0;
export const DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN = 1 << 1;
export const DIALOG_FLAG_SHOW_DIALOG_IMMERSIVE = 1 << 2;
export const DIALOG_FLAG_SHOW_CLOSE = 1 << 3;

export type DialogProps = {
  id?: string,
  flags?: number,
  args?: any[],
}

type ShowDialog = {
  id: string,
  flags: number,
  rendered: ReactElement,
  onClose?: () => void,
}

export interface Context {

  showDialog(props: DialogProps, render: (props: DialogProps) => ReactElement, onClose?: () => void): string;

  hideDialog(id?: string): void;

  hideAllDialogs(): void;
}

export type BaseSiteProps = {
  children?: ReactNode,
}

export type BaseSiteState = {
  showDialogs: ShowDialog[],
}

export abstract class BaseSite<P extends BaseSiteProps = BaseSiteProps, S extends BaseSiteState = BaseSiteState> extends Component<P, S> implements Context {

  static CONTEXT: Context;

  constructor(props: P, context: any) {
    super(props, context);
    this.state = this.onCreateState();
    BaseSite.CONTEXT = this;
  }

  protected onCreateState(): S {
    return {
      showDialogs: [],
    } as S;
  }

  protected abstract getTheme(): Theme;

  protected abstract getHeader(): ReactElement;

  protected abstract getFooter(): ReactElement;

  render() {
    return <ThemeProvider theme={this.getTheme()}>
      <div id="main" style={{display: "flex", flexDirection: "column", minHeight: "100vh"}}>
        {this.getHeader()}
        <div style={{display: "flex", flexDirection: "column", marginTop: 72, flexGrow: 1}}>
          {this.props.children}
          {this.state.showDialogs.reverse().map((showDialog, index) => (showDialog.flags & DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN) !== 0 ?
            this.renderFullscreenDialog(showDialog, index)
            : this.renderDialog(showDialog, index))}
        </div>
        {this.getFooter()}
      </div>
    </ThemeProvider>;
  }

  showDialog(props: DialogProps, render: (props: DialogProps) => ReactElement, onClose?: () => void): string {
    const id = props?.id || md5_uuid();
    if (!onClose) {
      onClose = () => BaseSite.CONTEXT.hideDialog(id);
    }
    let flags = props?.flags || 0;
    this.setState({
      showDialogs: [{
        id: id,
        flags: flags,
        rendered: render(props),
        onClose: onClose,
      } as ShowDialog,
        ...this.state.showDialogs]
    });
    return id;
  }

  hideDialog(id?: string): void {
    if (id) {
      const index = this.state.showDialogs.findIndex(showDialog => showDialog.id === id);
      if (index >= 0) {
        this.setState({
          showDialogs: [...this.state.showDialogs.slice(0, index), ...this.state.showDialogs.slice(index + 1)],
        });
      }
    } else {
      this.setState({
        showDialogs: this.state.showDialogs.slice(1),
      });
    }
  }

  hideAllDialogs(): void {
    this.setState({
      showDialogs: [],
    });
  }

  private renderFullscreenDialog(dialog: ShowDialog, index: number) {
    const immersive = (dialog.flags & DIALOG_FLAG_SHOW_DIALOG_IMMERSIVE) !== 0;
    const showClose = (dialog.flags & DIALOG_FLAG_SHOW_CLOSE) !== 0;
    return <Box style={{position: "absolute", top: 0, left: 0, bottom: 0, right: 0, zIndex: 1300 + index}}>
      <Backdrop style={{background: immersive ? black : white,}} open={true}>
        <Box width="100%" height="100dvh" style={{position: "relative"}}>
          {showClose ? this.renderCloseButton() : null}
          <Box style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            position: "absolute",
          }}>
            {dialog.rendered}
          </Box>
        </Box>
      </Backdrop>
    </Box>;
  }

  private renderDialog(dialog: ShowDialog, index: number) {
    let showClose = (dialog.flags & DIALOG_FLAG_SHOW_CLOSE) !== 0;
    return <Dialog
      style={{
        zIndex: 1300 + index,
      }}
      disableScrollLock={false}
      scroll="body"
      maxWidth="xl"
      open={true}
      onClose={(event, reason) => {
        if (reason === "backdropClick" && (dialog.flags & DIALOG_FLAG_DISABLE_BACKDROP_CLICK)) {
          return;
        }
        this.hideDialog();
      }}>
      {showClose ? this.renderCloseButton() : null}
      {dialog.rendered}
    </Dialog>;
  }

  private renderCloseButton(onClose?: () => void) {
    if (!onClose) {
      onClose = () => this.hideDialog();
    }
    return <Box style={{
      display: "flex",
      left: PD_XSM,
      right: PD_XSM,
      top: PD_XSM,
      alignItems: "center",
      justifyContent: "flex-end",
      height: SZ_SM,
      position: "absolute",
      zIndex: 2000,
    }}>
      <IconButton style={{
        width: SZ_SM,
        height: SZ_SM,
        background: lightGray,
      }} onClick={onClose}>
        <Close style={{color: "black"}}/>
      </IconButton>
    </Box>;
  }
}
